// ForgotPasswordForm — F8.7b · email → send password reset link.
// Backend is idempotent (always returns same response for enumeration protection).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthModal } from "@/context/AuthModalContext";

export default function ForgotPasswordForm({ prefillEmail = "" }) {
    const { t } = useTranslation("landing_v2");
    const { setTab } = useAuthModal();
    const [email, setEmail] = useState(prefillEmail);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await fetch(`/api/auth/forgot-password`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            // Regardless of outcome, show generic sent message.
            setSent(true);
        } catch {
            setSent(true);
        }
        setSubmitting(false);
    };

    if (sent) {
        return (
            <div className="text-center space-y-4 py-4" data-testid="auth-modal-forgot-sent">
                <div className="w-12 h-12 mx-auto rounded-full bg-brand-primary/15 border border-brand-primary/40 flex items-center justify-center">
                    <svg className="w-6 h-6 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                </div>
                <p className="text-text-secondary text-sm leading-relaxed">
                    {t("auth.modal.reset_sent")}
                </p>
                <button
                    type="button"
                    onClick={() => setTab("login")}
                    className="text-xs text-brand-primary hover:text-brand-secondary transition-colors"
                    data-testid="auth-modal-back-to-login-forgot"
                >
                    {t("auth.modal.toggle_to_login")}
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-modal-forgot-form">
            <div>
                <label htmlFor="forgot-email" className="block text-xs text-text-secondary mb-1.5">
                    {t("auth.modal.email_label")}
                </label>
                <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.modal.email_placeholder")}
                    data-testid="auth-modal-forgot-email"
                    className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                />
            </div>
            <button
                type="submit"
                disabled={submitting}
                data-testid="auth-modal-forgot-submit"
                className="w-full px-4 py-2.5 rounded-xl bg-white hover:bg-[#EDEDED] text-black font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? "…" : t("auth.modal.submit_forgot")}
            </button>
            <div className="text-center text-xs text-text-secondary pt-1">
                <button
                    type="button"
                    onClick={() => setTab("login")}
                    className="hover:text-text-main transition-colors"
                >
                    {t("auth.modal.toggle_to_login")}
                </button>
            </div>
        </form>
    );
}
